// Collection discovery layer of the V2 storefront: category-specific facets,
// dimension ranges, colour families and instant filtering without page loads.
//
// Data (format v3, platform/scripts/storefront-discovery-phase3):
//  - assets/sf-ix-<handle>.json, the compact filter index: every product's
//    filter facts (offline attributes such as colour, door type, family, plus
//    price, stock, vendor, dimensions and sort ranks) as dictionary-coded
//    columns in the collection's default order. Facets, counts, sorting,
//    family collapse and pagination run on it alone.
//  - assets/sf-cards-<handle>-NNN.json, card data (title, handle, frames,
//    dimensions) of consecutive positions, fetched only for the cards a page
//    shows (plus the next page, when the browser is idle).
// Both are static, CDN-cached and versioned. The server-rendered page carries
// a few live facts (count, in-stock count, the products of the rendered page);
// if they disagree with the index, the engine falls back to the live
// `<collection>?view=sf-data` pages (the v1 path) so results stay exact.
// The URL carries the state (shareable, back/forward), the base collection
// keeps its canonical URL. Any `filter.*` parameter keeps the native Shopify
// filters, and a failed load leaves the server-rendered page untouched.
//
// The pure part is exposed as window.ExxenDiscoveryCore for the Node tests.
(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;

  // ------------------------------------------------------------------ core
  const Core = (() => {
    const list = (fn) => ({ kind: 'list', get: fn });
    const bool = (fn) => ({ kind: 'bool', get: fn });
    const arr = (v) => (v === undefined || v === null || v === '' ? [] : [String(v)]);
    const brandKey = (value) => {
      const brand = String(value || '').toLowerCase();
      if (brand.includes('minar')) return 'Minar';
      if (brand.includes('yuru')) return 'Yuru';
      if (brand.includes('goldfalez') || brand.includes('falez')) return 'Falez';
      if (brand.includes('decortie')) return 'Decortie';
      return String(value || '');
    };
    const FACETS = {
      w: { kind: 'range', unit: 'cm', get: (it) => it.dims.w },
      h: { kind: 'range', unit: 'cm', get: (it) => it.dims.h },
      d: { kind: 'range', unit: 'cm', get: (it) => it.dims.d },
      price: { kind: 'range', unit: 'money', get: (it) => it.price },
      // Length of a ready kitchen set (merchant productType), from the index.
      setLength: { kind: 'range', unit: 'cm', get: (it) => it.rec.sl },
      brand: list((it) => arr(it.brand).map(brandKey)),
      color: list((it) => it.rec.c || []),
      room: list((it) => it.rec.r || []),
      group: list((it) => arr(it.group)),
      series: list((it) => arr(it.rec.s)),
      doorType: list((it) => arr(it.rec.dt)),
      doors: list((it) => arr(it.rec.dc)),
      module: list((it) => arr(it.rec.mt)),
      front: list((it) => arr(it.rec.fr)),
      finish: list((it) => arr(it.rec.fi)),
      shape: list((it) => arr(it.rec.sp)),
      seats: list((it) => arr(it.rec.se)),
      mount: list((it) => arr(it.rec.mo)),
      bedSize: list((it) => arr(it.rec.bs)),
      mirrored: bool((it) => it.rec.m === 1),
      drawers: bool((it) => it.rec.dr === 1),
      corner: bool((it) => it.rec.k === 1),
      shelves: bool((it) => it.rec.sh === 1),
      led: bool((it) => it.rec.led === 1),
      lift: bool((it) => it.rec.lf === 1),
      extendable: bool((it) => it.rec.ex === 1),
      withChairs: bool((it) => it.rec.ch === 1),
      storage: bool((it) => it.rec.st === 1),
      available: bool((it) => it.available === true)
    };
    const PARAMS = {
      w: 'w', h: 'h', d: 'd', price: 'pr', brand: 'brand', color: 'color', room: 'room', group: 'cat', series: 'series',
      doorType: 'door', doors: 'dc', module: 'mt', front: 'fr', finish: 'fi', shape: 'shape', seats: 'seats', mount: 'mount',
      bedSize: 'bed', mirrored: 'mir', drawers: 'drw', corner: 'corner', shelves: 'shelf', led: 'led', lift: 'lift',
      extendable: 'ext', withChairs: 'chairs', storage: 'storage', available: 'av', setLength: 'len'
    };
    // Filter values of links from before the URL language change (Turkish ids)
    // -> current English ids; old bookmarks and shared links keep working.
    const LEGACY = {
      doorType: { menteseli: 'hinged', surgulu: 'sliding' },
      module: { ust: 'upper', alt: 'base', boy: 'tall' },
      front: { kapakli: 'door', cekmeceli: 'drawer', cam: 'glass', 'ledli-cam': 'led-glass', yukluk: 'top-unit', aspirator: 'extractor', davlumbaz: 'hood', ankastre: 'built-in' },
      finish: { duz: 'flat' },
      shape: { dikdortgen: 'rectangular', yuvarlak: 'round', kare: 'square' },
      mount: { duvar: 'wall', zemin: 'floor' },
      color: { beyaz: 'white', antrasit: 'anthracite', siyah: 'black', gri: 'grey', 'acik-gri': 'light-grey', dore: 'dore', gold: 'gold', kumtasi: 'sandstone', kum: 'sand', ceviz: 'walnut', 'afrika-ceviz': 'african-walnut', kahverengi: 'brown', krem: 'cream', bej: 'beige', ekru: 'ecru', aytasi: 'moonstone', traverten: 'travertine', atlantik: 'atlantic', mavi: 'blue', pembe: 'pink', kirmizi: 'red', yesil: 'green', sari: 'yellow', turuncu: 'orange', mor: 'purple', gumus: 'silver', mese: 'oak', bor: 'pine', beton: 'concrete', mermer: 'marble', 'cok-renkli': 'multicolour' },
      room: { 'oturma-odasi': 'living-room', 'yatak-odasi': 'bedroom', 'yemek-odasi': 'dining-room', 'calisma-odasi': 'study-room', 'genc-cocuk-odasi': 'kids-and-youth-room', 'antre-hol': 'entryway-and-hall', mutfak: 'kitchen', 'ofis-home-office': 'office-and-home-office', banyo: 'bathroom', 'bahce-balkon': 'garden-and-balcony', aksesuarlar: 'accessories' },
      group: { 'alt-dolaplar-v2': 'base-cabinets', 'antre-dolaplari': 'entryway-cabinets', askiliklar: 'coat-racks', ayakkabiliklar: 'shoe-cabinets', aynalar: 'mirrors', 'bahce-ve-balkon-mobilyasi': 'garden-and-balcony-furniture', 'banyo-dolaplari': 'bathroom-cabinets', 'banyo-duzenleyiciler': 'bathroom-organizers', 'bar-masalari': 'bar-tables', basliklar: 'headboards', 'baza-baslik-setleri': 'bed-base-and-headboard-sets', bazalar: 'bed-bases', 'boy-dolaplari': 'tall-cabinets', 'calisma-masalari-v2': 'desks', 'calisma-ve-oyuncu-koltuklari': 'office-and-gaming-chairs', 'camasir-makinesi-dolaplari': 'washing-machine-cabinets', 'cocuk-kitapliklari': 'kids-bookcases', 'cocuk-odasi-takimlari': 'kids-room-sets', 'donanim-ve-baglanti': 'hardware-and-fittings', 'duvar-dekorasyonu': 'wall-decor', 'duvar-raflari': 'wall-shelves', 'evye-dolaplari': 'sink-cabinets', 'firin-ve-ankastre-modulleri': 'oven-and-built-in-modules', gardiroplar: 'wardrobes', 'genc-odasi-takimlari': 'youth-room-sets', 'kanepe-ve-koltuklar': 'sofas-and-armchairs', karyolalar: 'beds', 'keson-ve-dosya-dolaplari': 'pedestals-and-file-cabinets', kitapliklar: 'bookcases', 'koltuk-takimlari': 'sofa-sets', komodinler: 'nightstands', konsollar: 'consoles', 'kose-dolaplar': 'corner-cabinets', kulplar: 'handles', 'makyaj-masalari': 'vanity-tables', 'mobilya-aksesuarlari': 'furniture-accessories', 'mobilya-ayaklari': 'furniture-legs', 'mutfak-aksesuarlari': 'kitchen-accessories', 'mutfak-depolama': 'kitchen-storage', 'mutfak-dolabi-takimlari': 'kitchen-cabinet-sets', 'mutfak-dolaplari-v2': 'kitchen-cabinets', 'ofis-dolaplari': 'office-cabinets', 'ofis-masalari': 'office-desks', 'ofis-takimlari': 'office-sets', 'orta-sehpalar': 'coffee-tables', 'oturma-odasi-takimlari': 'living-room-sets', portmantolar: 'hall-stands', 'puf-ve-tabureler': 'poufs-and-stools', 'ray-ve-menteseler': 'rails-and-hinges', sandalyeler: 'chairs', sifonyerler: 'dressers', 'tamamlayici-parcalar': 'add-on-parts', 'tv-uniteleri-v2': 'tv-units', 'ust-dolaplar-v2': 'wall-cabinets', vitrinler: 'display-cabinets', 'yan-sehpalar': 'side-tables', 'yatak-odasi-takimlari': 'bedroom-sets', yataklar: 'mattresses', 'yemek-masalari-v2': 'dining-tables', 'yemek-odasi-takimlari': 'dining-room-sets', 'zigon-sehpalar': 'nesting-tables' }
    };
    const OWN_PARAMS = new Set([...Object.values(PARAMS), 'page', 'sort_by']);
    const PRESET_STEP = { w: 50, h: 50, d: 20, setLength: 50 };

    // custom.dimensions as the filter trusts it (same rule as the offline
    // index): unit cm (or none), every axis 0 < n < 1000.
    const DIM_AXES = ['width', 'height', 'depth', 'length', 'diameter', 'thickness'];
    function parseDims(dm) {
      const out = {};
      if (!dm || typeof dm !== 'object') return out;
      if (dm.unit && String(dm.unit).toLowerCase() !== 'cm') return out;
      for (const [axis, key] of [['width', 'w'], ['height', 'h'], ['depth', 'd']]) {
        const n = Number(dm[axis]);
        if (dm[axis] !== undefined && dm[axis] !== null && dm[axis] !== '' && Number.isFinite(n) && n > 0 && n < 1000) out[key] = Math.round(n * 10) / 10;
      }
      return out;
    }

    const collator = (locale) => {
      try { return new Intl.Collator(locale || undefined, { sensitivity: 'base', numeric: true }); } catch { return new Intl.Collator(); }
    };
    // Dense rank: equal keys share a rank, ties are broken by position.
    function denseRank(keys, compare) {
      const order = keys.map((_, i) => i).sort((a, b) => compare(keys[a], keys[b]) || a - b);
      const rank = new Array(keys.length);
      let r = 0;
      order.forEach((pos, i) => { if (i > 0 && compare(keys[order[i - 1]], keys[pos]) !== 0) r += 1; rank[pos] = r; });
      return rank;
    }

    // ------------------------------------------------------------ index v2
    const CODES = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ-_';
    const CODE_AT = {};
    for (let i = 0; i < CODES.length; i += 1) CODE_AT[CODES[i]] = i;
    // {d, s}: dictionary + one code character per product; {d, x}: dictionary
    // + indexes; {r}: raw values. null = no value.
    function decodeColumn(col, n) {
      if (!col) return new Array(n).fill(null);
      if (col.r) return col.r;
      if (col.x) return col.x.map((i) => col.d[i]);
      const out = new Array(n);
      for (let i = 0; i < n; i += 1) out[i] = col.d[CODE_AT[col.s[i]]];
      return out;
    }
    const titleRankKey = (ix, locale) => {
      const loc = String(locale || '').toLowerCase().split('-')[0];
      const tr = ix.col.tr || {};
      if (tr[loc]) return loc;
      if (ix.trAlias && ix.trAlias[loc] && tr[ix.trAlias[loc]]) return ix.trAlias[loc];
      return tr.bg ? 'bg' : Object.keys(tr)[0];
    };
    function itemOf(id, pos, rec, groups) {
      return {
        id, ord: pos, pos, rec, L: null,
        group: Number.isInteger(rec.g) ? groups[rec.g] || null : null,
        fam: Number.isInteger(rec.f) ? rec.f : null
      };
    }
    // Every product of the index, in the default order, with the facts the
    // filters, counts and sorts read; `L` (card data) is attached per chunk.
    function decodeIndex(ix, locale) {
      const n = ix.n;
      const col = (c) => decodeColumn(c, n);
      const p = col(ix.col.p); const cp = col(ix.col.cp); const a = col(ix.col.a); const b = col(ix.col.b);
      const w = col(ix.col.w); const h = col(ix.col.h); const d = col(ix.col.d); const cr = col(ix.col.cr);
      const tr = col((ix.col.tr || {})[titleRankKey(ix, locale)]);
      const recCols = Object.keys(ix.rec || {}).map((k) => [k, col(ix.rec[k])]);
      const groups = ix.groups || [];
      const items = new Array(n);
      for (let i = 0; i < n; i += 1) {
        const rec = {};
        for (const [k, vals] of recCols) if (vals[i] !== null) rec[k] = vals[i];
        const dims = {};
        if (w[i] !== null) dims.w = w[i];
        if (h[i] !== null) dims.h = h[i];
        if (d[i] !== null) dims.d = d[i];
        const it = itemOf(ix.id[i], i, rec, groups);
        it.price = p[i] / 100; it.cents = p[i]; it.cp = cp[i]; it.brand = b[i]; it.available = a[i] === 1; it.dims = dims;
        it.tr = tr[i]; it.cr = cr[i];
        items[i] = it;
      }
      const byId = new Map(items.map((it) => [it.id, it]));
      const families = (ix.fam || []).map((ms) => ms.map((m) => (m < n ? items[m] : byId.get(m))).filter(Boolean));
      return { items, byId, families, ix, mode: 'index' };
    }
    // The v1 path, used when the live facts of the page disagree with the
    // index: the live sf-data rows decide membership, order, price, stock,
    // vendor, dimensions, titles and dates; the index still gives the offline
    // facts (by product id).
    function buildLiveItems(ix, live, locale) {
      const recs = new Map();
      const recCols = Object.keys(ix.rec || {}).map((k) => [k, decodeColumn(ix.rec[k], ix.n)]);
      ix.id.forEach((id, i) => {
        const rec = {};
        for (const [k, vals] of recCols) if (vals[i] !== null) rec[k] = vals[i];
        recs.set(id, rec);
      });
      for (const r of ix.xrec || []) { const { i, ...rec } = r; recs.set(i, rec); }
      const groups = ix.groups || [];
      const seen = new Set();
      const items = [];
      for (const L of live) {
        if (!L || seen.has(L.i)) continue;
        seen.add(L.i);
        const it = itemOf(L.i, items.length, recs.get(L.i) || {}, groups);
        it.L = L;
        it.price = Number(L.p || 0) / 100; it.cents = Number(L.p || 0); it.cp = Number(L.cp || 0);
        it.brand = L.b || ''; it.available = L.a === 1; it.dims = parseDims(L.dm);
        items.push(it);
      }
      const col = collator(locale);
      const tr = denseRank(items.map((it) => it.L.t || ''), (x, y) => col.compare(x, y));
      const cr = denseRank(items.map((it) => it.L.ts || 0), (x, y) => x - y);
      items.forEach((it, i) => { it.tr = tr[i]; it.cr = cr[i]; });
      const byId = new Map(items.map((it) => [it.id, it]));
      const families = (ix.fam || []).map((ms) => ms.map((m) => byId.get(m < ix.n ? ix.id[m] : m)).filter(Boolean));
      return { items, byId, families, ix, mode: 'live' };
    }
    // Card chunks: chunk of a position, positions of a chunk.
    function chunkOf(pos, ix) {
      const c = ix.cards;
      return Math.floor((c.at ? c.at[pos] : pos) / c.size);
    }
    function chunkPositions(k, ix) {
      const c = ix.cards;
      if (!c.at) { const out = []; for (let p = k * c.size; p < Math.min(ix.n, (k + 1) * c.size); p += 1) out.push(p); return out; }
      const out = [];
      c.at.forEach((slot, p) => { if (Math.floor(slot / c.size) === k) out[slot % c.size] = p; });
      return out;
    }
    // Chunk row -> the card fields sf-data gives (same names), with the
    // commerce facts of the index.
    function cardFromRow(row, it, base) {
      const [handle, titles, variants, frames, iw, ih, dm, cards] = row;
      const title = titles && typeof titles === 'object' ? titles[base.locale] || titles.bg || titles.tr || titles.en || '' : titles;
      const L = { i: it.id, u: `${base.productBase}${handle}`, h: handle, t: title, b: it.brand, p: it.cents, cp: it.cp, a: it.available ? 1 : 0, n: variants };
      L.html = cards && typeof cards === 'object' ? cards[base.locale] || cards.bg || cards.tr || cards.en || '' : cards || '';
      // A frame outside the common CDN prefix is stored whole ("//…", "https:…").
      L.im = (frames || []).map((s) => (s.charAt(0) === '/' || /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : base.img[0] + s + base.img[1]));
      if (iw) L.iw = iw;
      if (ih) L.ih = ih;
      // [w, h, d] (cm) or, when a record holds more, [w, h, d, l, dia, t, unit].
      if (Array.isArray(dm)) {
        L.dm = {};
        DIM_AXES.forEach((axis, i) => { if (dm[i] !== null && dm[i] !== undefined) L.dm[axis] = dm[i]; });
        if (typeof dm[6] === 'string') L.dm.unit = dm[6];
      } else L.dm = null;
      return L;
    }

    // ---------------------------------------------------------------- URL state
    const num = (s) => {
      if (s === undefined || s === null || s === '') return null;
      const n = Number(String(s).replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    };
    function parseRange(raw) {
      if (!raw) return null;
      const m = String(raw).match(/^\s*([\d.,]*)\s*-\s*([\d.,]*)\s*$/);
      if (!m) return null;
      let min = num(m[1]); let max = num(m[2]);
      if (min === null && max === null) return null;
      if (min !== null && max !== null && min > max) [min, max] = [max, min];
      return { min, max };
    }
    function parseQuery(search, facets) {
      const params = new URLSearchParams(search || '');
      const f = {};
      for (const key of facets) {
        const def = FACETS[key];
        const raw = params.get(PARAMS[key]);
        if (!def || raw === null || raw === '') continue;
        if (def.kind === 'range') { const r = parseRange(raw); if (r) f[key] = r; }
        else if (def.kind === 'bool') { if (raw === '1' || raw === 'true') f[key] = true; }
        else { const vals = raw.split(',').map((v) => v.trim()).filter(Boolean).map((v) => (LEGACY[key] && LEGACY[key][v]) || (key === 'brand' ? brandKey(v) : v)); if (vals.length) f[key] = [...new Set(vals)]; }
      }
      const page = Math.max(1, parseInt(params.get('page'), 10) || 1);
      const sort = params.get('sort_by') || null;
      return { f, page, sort };
    }
    const fmtNum = (n) => (n === null || n === undefined ? '' : String(Math.round(n * 10) / 10));
    // Query string of the state, other (foreign) parameters kept in place.
    function toQuery(state, facets, search, defaultSort) {
      const keep = [];
      for (const [k, v] of new URLSearchParams(search || '')) if (!OWN_PARAMS.has(k)) keep.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      const own = [];
      for (const key of facets) {
        const val = state.f[key];
        if (val === undefined) continue;
        const def = FACETS[key];
        if (def.kind === 'range') own.push(`${PARAMS[key]}=${fmtNum(val.min)}-${fmtNum(val.max)}`);
        else if (def.kind === 'bool') own.push(`${PARAMS[key]}=1`);
        else if (val.length) own.push(`${PARAMS[key]}=${val.map((v) => encodeURIComponent(v)).join(',')}`);
      }
      if (state.sort && state.sort !== defaultSort) own.push(`sort_by=${encodeURIComponent(state.sort)}`);
      if (state.page > 1) own.push(`page=${state.page}`);
      const all = [...keep, ...own];
      return all.length ? `?${all.join('&')}` : '';
    }

    // ---------------------------------------------------------------- filtering
    function test(it, key, val) {
      const def = FACETS[key];
      if (!def) return true;
      if (def.kind === 'range') {
        const v = def.get(it);
        if (v === undefined || v === null) return false; // missing axis: out only while the range is active
        if (val.min !== null && val.min !== undefined && v < val.min) return false;
        if (val.max !== null && val.max !== undefined && v > val.max) return false;
        return true;
      }
      if (def.kind === 'bool') return !!def.get(it);
      const vs = def.get(it);
      for (const v of vs) if (val.includes(v)) return true;
      return false;
    }
    function matches(it, f, except) {
      for (const key in f) {
        if (key === except) continue;
        if (!test(it, key, f[key])) return false;
      }
      return true;
    }

    // Sort in place-safe copy. `order` (id -> rank) is the server order of a
    // sort only Shopify knows (manual, best-selling); price, title and date
    // sort on the client, title and date on the dense ranks of the index
    // (title: the storefront locale's collation, same comparator as before).
    function sortItems(items, sort, ctx) {
      const out = items.slice();
      const byOrd = (a, b) => a.ord - b.ord;
      const byId = (a, b) => a.id - b.id;
      const rank = ctx.orders && ctx.orders[sort];
      if (rank) return out.sort((a, b) => (rank.has(a.id) ? rank.get(a.id) : 1e9) - (rank.has(b.id) ? rank.get(b.id) : 1e9) || byOrd(a, b));
      switch (sort) {
        case 'price-ascending': return out.sort((a, b) => a.price - b.price || byOrd(a, b));
        case 'price-descending': return out.sort((a, b) => b.price - a.price || byOrd(a, b));
        case 'title-ascending': return out.sort((a, b) => a.tr - b.tr || byId(a, b));
        case 'title-descending': return out.sort((a, b) => b.tr - a.tr || byId(a, b));
        case 'created-ascending': return out.sort((a, b) => a.cr - b.cr || byOrd(a, b));
        case 'created-descending': return out.sort((a, b) => b.cr - a.cr || byOrd(a, b));
        default: return out.sort(byOrd);
      }
    }
    const clientSort = () => false;

    // One card per family: the first family member in sort order that matches
    // every filter represents it; the swatches are the members that match
    // every filter except colour (so the choice stays inside the other facets).
    function collapse(sorted, f, data) {
      const cards = [];
      const seen = new Set();
      for (const it of sorted) {
        if (it.fam === null || !data.families[it.fam] || data.families[it.fam].length < 2) { cards.push({ rep: it, members: [] }); continue; }
        if (seen.has(it.fam)) continue;
        seen.add(it.fam);
        const members = data.families[it.fam].filter((m) => m === it || matches(m, f, 'color'));
        cards.push({ rep: it, members: members.length > 1 ? members : [] });
      }
      return cards;
    }

    function facetInfo(key, base) {
      const def = FACETS[key];
      if (def.kind === 'range') {
        let n = 0; let min = Infinity; let max = -Infinity;
        for (const it of base) { const v = def.get(it); if (v === undefined || v === null) continue; n += 1; if (v < min) min = v; if (v > max) max = v; }
        return { kind: 'range', count: n, min: n ? min : null, max: n ? max : null };
      }
      if (def.kind === 'bool') {
        let n = 0; for (const it of base) if (def.get(it)) n += 1;
        return { kind: 'bool', count: n };
      }
      const counts = {};
      for (const it of base) for (const v of def.get(it)) counts[v] = (counts[v] || 0) + 1;
      return { kind: 'list', counts };
    }

    // Everything one render needs: matched products, disjunctive facet counts
    // (each facet counted with all the other facets applied), cards, page.
    function compute(data, state, ctx) {
      const f = state.f;
      const active = Object.keys(f);
      const matched = data.items.filter((it) => matches(it, f));
      const info = {};
      for (const key of ctx.facets) {
        if (!FACETS[key]) continue;
        const base = active.includes(key) ? data.items.filter((it) => matches(it, f, key)) : matched;
        info[key] = facetInfo(key, base);
      }
      const sorted = sortItems(matched, state.sort || ctx.defaultSort, ctx);
      const cards = collapse(sorted, f, data);
      const pageSize = ctx.pageSize || 24;
      const pages = Math.max(1, Math.ceil(cards.length / pageSize));
      const page = Math.min(Math.max(1, state.page || 1), pages);
      return { matched, info, cards, pages, page, pageCards: cards.slice((page - 1) * pageSize, page * pageSize) };
    }

    // Zero results: which single facet to drop, with how many products it gives back.
    function suggestions(data, state) {
      const out = [];
      for (const key of Object.keys(state.f)) {
        const n = data.items.filter((it) => matches(it, state.f, key)).length;
        if (n > 0) out.push({ key, count: n });
      }
      return out.sort((a, b) => b.count - a.count).slice(0, 4);
    }

    // Quick range buckets (e.g. width 150-200 cm) over the whole collection.
    function presets(key, data) {
      const step = PRESET_STEP[key];
      if (!step) return [];
      const values = data.items.map((it) => FACETS[key].get(it)).filter((v) => v !== undefined && v !== null);
      if (values.length < 8) return [];
      const lo = Math.floor(Math.min(...values) / step) * step;
      const hi = Math.ceil(Math.max(...values) / step) * step;
      const out = [];
      for (let a = lo; a < hi; a += step) out.push({ min: a, max: a + step });
      if (out.length > 6) {
        // keep the five busiest-contiguous head buckets, fold the tail into "N+"
        const cut = out[4].max;
        out.length = 5;
        out.push({ min: cut, max: null });
      }
      return out;
    }

    // ---------------------------------------------------------------- rendering
    const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const fill = (template, values) => {
      let out = String(template || '');
      for (const [k, v] of Object.entries(values)) out = out.split(k).join(String(v));
      return out;
    };
    function groupDigits(cents, places, thousand, decimal) {
      const value = (Math.abs(Number(cents) || 0) / 100).toFixed(places);
      const [whole, fraction] = value.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
      return fraction ? grouped + decimal + fraction : grouped;
    }
    function formatMoney(cents, format) {
      const amount = Number(cents) || 0;
      const sign = amount < 0 ? '-' : '';
      return sign + String(format || '{{amount_with_comma_separator}} €').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
        switch (name) {
          case 'amount_no_decimals': return groupDigits(amount, 0, ',', '.');
          case 'amount_with_comma_separator': return groupDigits(amount, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return groupDigits(amount, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return groupDigits(amount, 2, "'", '.');
          default: return groupDigits(amount, 2, ',', '.');
        }
      });
    }
    const icon = (name, cls = 'sf-i') => {
      const size = cls ? 16 : 20;
      return `<svg class="${cls}" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href="#i-${name}"></use></svg>`;
    };
    function shownDim(v) {
      const r2 = Math.round(v * 100) / 100;
      const whole = Math.round(v);
      return r2 === whole ? String(whole) : String(r2).replace('.', ',');
    }
    // Same rule as snippets/sf-dims.liquid: every axis > 0, diameter first,
    // then W/H/D/L and thickness, in the record's own unit (none = cm).
    const CARD_AXES = [['diameter', 'dia'], ['width', 'w'], ['height', 'h'], ['depth', 'd'], ['length', 'l'], ['thickness', 't']];
    function renderDims(dm, ctx) {
      if (!dm || typeof dm !== 'object') return '';
      const S = ctx.strings;
      const axes = CARD_AXES.filter(([axis]) => Number(dm[axis]) > 0);
      if (!axes.length) return '';
      const unit = ['mm', 'm', 'in'].includes(dm.unit) ? dm.unit : 'cm';
      const pattern = S[unit] || S.cm;
      return `<dl class="sf-dims" aria-label="${esc(S.dimensions)}">${axes.map(([axis, key]) => {
        const label = ctx.labels[key] || '';
        return `<div class="sf-dims__item" title="${esc(label)}"><dt>${icon(axis)}<span class="visually-hidden">${esc(label)}</span></dt><dd>${esc(fill(pattern, { __V__: shownDim(Number(dm[axis])) }))}</dd></div>`;
      }).join('')}</dl>`;
    }
    function colorLabel(keys, ctx) {
      return keys.map((k) => (ctx.colors[k] ? ctx.colors[k][0] : k)).join(' / ');
    }
    function swatchPaint(keys, ctx) {
      const hexes = keys.map((k) => (ctx.colors[k] ? ctx.colors[k][1] : '')).filter(Boolean);
      if (!hexes.length || hexes.length !== keys.length) return '';
      if (hexes.length === 1) return hexes[0];
      const step = 100 / hexes.length;
      return `linear-gradient(135deg, ${hexes.map((h, i) => `${h} ${Math.round(i * step)}% ${Math.round((i + 1) * step)}%`).join(', ')})`;
    }
    const SWATCH_LIMIT = 5;
    function sizedImage(url, width) {
      if (!url) return '';
      try { const next = new URL(url, window.location.origin); next.searchParams.set('width', String(width)); return next.toString(); } catch { return url; }
    }
    function renderSwatches(members, activeId, ctx) {
      if (members.length < 2) return '';
      let shown = members.slice(0, SWATCH_LIMIT);
      const active = members.find((m) => m.id === activeId);
      if (active && !shown.includes(active)) shown = [...shown.slice(0, SWATCH_LIMIT - 1), active];
      const more = members.length - shown.length;
      return `<div class="sf-swatches sf-swatches--dx" role="group" aria-label="${esc(ctx.strings.swatches)}">${shown.map((m) => {
        const keys = m.rec.c || [];
        const name = colorLabel(keys, ctx);
        const photo = m.L && Array.isArray(m.L.im) ? m.L.im[0] : '';
        const paint = swatchPaint(keys, ctx);
        const sourcePhoto = photo ? sizedImage(photo, 96) : '';
        const tokenPaints = keys.map((key) => (ctx.colors[key] ? ctx.colors[key][1] : ''));
        const knownIndexes = tokenPaints.map((value, index) => value ? index : -1).filter((index) => index >= 0);
        let source = paint ? 'mapped' : sourcePhoto ? 'variant-image' : 'missing';
        let style = paint ? `--swatch:${esc(paint)}` : sourcePhoto ? `--swatch-image:url('${esc(sourcePhoto)}')` : '';
        if (!paint && sourcePhoto && keys.length === 2 && knownIndexes.length === 1) {
          const knownIndex = knownIndexes[0];
          const mask = knownIndex === 0
            ? `linear-gradient(90deg,${tokenPaints[knownIndex]} 0 50%,transparent 50%)`
            : `linear-gradient(90deg,transparent 0 50%,${tokenPaints[knownIndex]} 50%)`;
          style = `--swatch-image:${esc(mask)},url('${esc(sourcePhoto)}')`;
          source = 'split-variant-image';
        }
        return `<button type="button" class="sf-swatch sf-swatch--btn${source === 'missing' ? ' sf-swatch--missing' : ''}" data-swatch-source="${source}"${style ? ` style="${style}"` : ''} title="${esc(name)}" aria-label="${esc(fill(ctx.strings.swatch, { __L__: name }))}" aria-pressed="${m.id === activeId}" data-dx-swatch="${m.id}"></button>`;
      }).join('')}${more > 0 ? `<span class="sf-swatch-more" aria-hidden="true">+${more}</span>` : ''}</div><span class="sf-swatches__selected" data-card-variant-label>${esc(colorLabel((active || members[0]).rec.c || [], ctx))}</span>`;
    }
    // Mirrors snippets/sf-product-card.liquid element for element.
    function renderCard(it, members, ctx, activeId, eager = false) {
      const L = it.L;
      const S = ctx.strings;
      const title = L.t || '';
      const url = L.u || '#';
      if (L.html) {
        const pressed = ctx.wishlist && ctx.wishlist.has(L.h);
        let html = String(L.html).replace('<article class="sf-card"', `<article class="sf-card" data-dx-card="${it.id}"${members.length > 1 ? ` data-dx-family="${it.fam}"` : ''}`);
        if (!eager) html = html.replace('loading="eager"', 'loading="lazy"');
        if (pressed) html = html.replace('class="sf-fav sf-card__fav"', 'class="sf-fav sf-card__fav is-active"').replace('aria-pressed="false"', 'aria-pressed="true"');
        const family = renderSwatches(members, activeId ?? it.id, ctx);
        if (family) html = html.replace('<div class="sf-card__foot">', `${family}<div class="sf-card__foot">`);
        return html;
      }
      // Never a file that is not a product photo (snippets/sf-image-rules.liquid);
      // a cover under 300px is shown at its own size, never upscaled.
      const frames = (Array.isArray(L.im) ? L.im : []).filter((u) => !(ctx.notPhoto || []).some((key) => key && String(u).includes(key)));
      const natural = frames.length && L.iw && L.ih && Math.min(L.iw, L.ih) < 300 ? ' sf-card__img--natural' : '';
      const price = Number(L.p || 0);
      const compare = Number(L.cp || 0);
      const discount = compare > price ? Math.round(((compare - price) * 100) / compare) : 0;
      const img = frames.length
        ? `<img src="${esc(sizedImage(frames[0], 480))}" srcset="${esc(sizedImage(frames[0], 240))} 240w, ${esc(sizedImage(frames[0], 360))} 360w, ${esc(sizedImage(frames[0], 480))} 480w, ${esc(sizedImage(frames[0], 640))} 640w" alt="${esc(title)}" width="${esc(L.iw || 800)}" height="${esc(L.ih || 600)}" class="sf-card__img sf-card__img--contain${natural}" loading="${eager ? 'eager' : 'lazy'}" decoding="async"${eager ? ' fetchpriority="high"' : ''} sizes="(min-width: 1200px) 320px, (min-width: 768px) 33vw, 50vw">`
        : `<img src="${esc(ctx.placeholder)}" alt="${esc(title)}" width="800" height="600" class="sf-card__img sf-card__img--contain" loading="lazy" decoding="async">`;
      const gallery = frames.length > 1 ? ` data-card-gallery data-images="${esc(JSON.stringify(frames.slice(0, 6).map((u) => [u, 'contain'])))}"` : '';
      const nav = frames.length > 1
        ? `<button type="button" class="sf-card__nav sf-card__nav--prev" data-card-step="-1" aria-label="${esc(S.prevImage)}">${icon('prev', '')}</button><button type="button" class="sf-card__nav sf-card__nav--next" data-card-step="1" aria-label="${esc(S.nextImage)}">${icon('next', '')}</button><span class="sf-card__dots" aria-hidden="true">${frames.slice(0, 6).map((_, i) => `<span class="sf-card__dot${i === 0 ? ' is-active' : ''}"></span>`).join('')}</span>`
        : '';
      const groupTitle = it.group && ctx.groups[it.group] ? ctx.groups[it.group] : '';
      const sale = compare > price;
      const priceHtml = `<div class="sf-price${sale ? ' sf-price--sale' : ''} sf-card__price"><span class="sf-price__current" data-price-current>${esc(formatMoney(price, ctx.moneyFormat))}</span>${sale ? `<s class="sf-price__compare" data-price-compare><span class="visually-hidden">${esc(S.previousPrice)}</span>${esc(formatMoney(compare, ctx.moneyFormat))}</s>` : ''}</div>`;
      const pressed = ctx.wishlist && ctx.wishlist.has(L.h);
      return `<article class="sf-card" data-dx-card="${it.id}"${members.length > 1 ? ` data-dx-family="${it.fam}"` : ''}>
      <div class="sf-card__media"${gallery}>
        <a class="sf-card__link" href="${esc(url)}" tabindex="-1" aria-hidden="true">${img}</a>${discount > 0 ? `<span class="sf-card__badge">${esc(fill(S.discount, { __P__: discount }))}</span>` : ''}${L.n > 1 ? `<span class="sf-card__options">${esc(fill(S.options, { __N__: L.n }))}</span>` : ''}<button type="button" class="sf-fav sf-card__fav${pressed ? ' is-active' : ''}" data-wishlist="${esc(L.h)}" aria-pressed="${pressed ? 'true' : 'false'}" aria-label="${esc(fill(S.addToFavorites, { __T__: title }))}">${icon('heart', '')}</button>${nav}
      </div>
      <div class="sf-card__body">
        <p class="sf-card__meta"><span class="sf-card__brand">${esc(L.b || '')}</span>${groupTitle ? `<span class="sf-card__group">${esc(groupTitle)}</span>` : ''}</p>
        <h3 class="sf-card__title"><a href="${esc(url)}">${esc(title)}</a></h3>
        ${renderDims(L.dm, ctx)}
        ${renderSwatches(members, activeId ?? it.id, ctx)}
        <div class="sf-card__foot">${priceHtml}</div>
      </div>
    </article>`;
    }

    // Value label of a list facet.
    function valueLabel(key, value, ctx) {
      const S = ctx.strings;
      switch (key) {
        case 'brand': {
          const brand = String(value || '').toLowerCase();
          if (brand.includes('minar')) return 'Minar';
          if (brand.includes('yuru')) return 'Yuru';
          if (brand.includes('goldfalez') || brand.includes('falez')) return 'Falez';
          if (brand.includes('decortie')) return 'Decortie';
          return value;
        }
        case 'color': return ctx.colors[value] ? ctx.colors[value][0] : value;
        case 'room': return ctx.rooms[value] || value;
        case 'group': return ctx.groups[value] || value;
        case 'series': return (ctx.series && ctx.series[value]) || value;
        case 'doors': return value === '1' ? S.doorsOne : fill(S.doorsN, { __N__: value });
        case 'seats': return fill(S.seatsN, { __N__: value });
        case 'bedSize': return `${String(value).replace('x', ' × ')} ${S.unitCm}`;
        default: return (ctx.enums[key] && ctx.enums[key][value]) || value;
      }
    }
    // Stable value order of a list facet.
    function orderValues(key, values, counts, ctx) {
      const col = collator(ctx.locale);
      const pos = (map) => { const ks = Object.keys(map || {}); return (v) => { const i = ks.indexOf(v); return i < 0 ? 1e6 : i; }; };
      switch (key) {
        case 'doors': case 'seats': return values.sort((a, b) => Number(a) - Number(b));
        case 'bedSize': return values.sort((a, b) => col.compare(a, b));
        case 'color': return values.sort((a, b) => (counts[b] || 0) - (counts[a] || 0) || col.compare(valueLabel(key, a, ctx), valueLabel(key, b, ctx)));
        case 'group': { const p = pos(ctx.groups); return values.sort((a, b) => p(a) - p(b)); }
        case 'room': { const p = pos(ctx.rooms); return values.sort((a, b) => p(a) - p(b)); }
        case 'brand': case 'series': return values.sort((a, b) => col.compare(valueLabel(key, a, ctx), valueLabel(key, b, ctx)));
        default: { const p = pos(ctx.enums[key]); return values.sort((a, b) => p(a) - p(b) || col.compare(a, b)); }
      }
    }
    function rangeLabel(key, val, ctx) {
      const S = ctx.strings;
      const f = (n) => (key === 'price' ? formatMoney(Math.round(n * 100), ctx.moneyFormat) : `${shownDim(n)} ${S.unitCm}`);
      const lo = val.min !== null && val.min !== undefined; const hi = val.max !== null && val.max !== undefined;
      let text;
      if (lo && hi) text = key === 'price' ? `${f(val.min)} – ${f(val.max)}` : `${shownDim(val.min)}–${shownDim(val.max)} ${S.unitCm}`;
      else if (lo) text = `${S.rangeFrom.toLowerCase()} ${f(val.min)}`;
      else text = `${S.rangeTo.toLowerCase()} ${f(val.max)}`;
      return `${ctx.labels[key]}: ${text}`;
    }
    // One entry per removable chip.
    function chips(state, ctx) {
      const out = [];
      for (const key of ctx.facets) {
        const val = state.f[key];
        if (val === undefined || !FACETS[key]) continue;
        const kind = FACETS[key].kind;
        if (kind === 'range') out.push({ key, label: rangeLabel(key, val, ctx) });
        else if (kind === 'bool') out.push({ key, label: ctx.labels[key] });
        else for (const v of val) out.push({ key, value: v, label: valueLabel(key, v, ctx) });
      }
      return out;
    }
    function without(state, key, value) {
      const f = { ...state.f };
      if (value !== undefined && Array.isArray(f[key])) {
        const rest = f[key].filter((v) => v !== value);
        if (rest.length) f[key] = rest; else delete f[key];
      } else delete f[key];
      return { ...state, f, page: 1 };
    }

    return {
      FACETS, PARAMS, LEGACY, parseDims, decodeColumn, decodeIndex, buildLiveItems, denseRank, chunkOf, chunkPositions, cardFromRow, parseQuery, toQuery, parseRange, test, matches, sortItems, clientSort, collapse, compute,
      suggestions, presets, renderCard, renderDims, renderSwatches, valueLabel, orderValues, rangeLabel, chips, without, formatMoney, esc, fill, shownDim
    };
  })();
  root.ExxenDiscoveryCore = Core;

  // ------------------------------------------------------------------ DOM
  if (typeof document === 'undefined' || !document.getElementById) return;
  const configNode = document.getElementById('ExxenDiscoveryConfig');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  const returnStorageKey = 'sf:collection-return';
  const currentLocation = () => `${window.location.pathname}${window.location.search}`;
  const saveReturnPosition = (link = null) => {
    const card = link?.closest?.('[data-dx-card], .sf-card');
    try { window.sessionStorage.setItem(returnStorageKey, JSON.stringify({
      url: currentLocation(), y: Math.max(0, Math.round(window.scrollY)), at: Date.now(),
      product: link ? new URL(link.href, window.location.href).pathname : null,
      cardOffset: card ? Math.round(card.getBoundingClientRect().top) : null
    })); } catch { /* storage unavailable */ }
  };
  const readReturnPosition = (persisted = false) => {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(returnStorageKey) || 'null');
      const navigation = performance.getEntriesByType('navigation')[0];
      return (persisted || navigation?.type === 'back_forward') && value?.url === currentLocation() && Date.now() - Number(value.at || 0) < 1800000 ? value : null;
    } catch { return null; }
  };
  const restoreReturnPosition = (persisted = false) => {
    const returnPosition = readReturnPosition(persisted);
    if (!returnPosition) return;
    // The shopper always wins: any scroll or tap intent ends the restore loop.
    const intents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    let userTookOver = false;
    const takeOver = () => { userTookOver = true; };
    intents.forEach((type) => window.addEventListener(type, takeOver, { capture: true, passive: true }));
    const finish = () => {
      intents.forEach((type) => window.removeEventListener(type, takeOver, { capture: true }));
      try { window.sessionStorage.removeItem(returnStorageKey); } catch { /* storage unavailable */ }
    };
    // Jump, never glide: html { scroll-behavior: smooth } would animate every correction.
    const jumpTo = (top) => {
      const style = document.documentElement.style;
      const previous = style.scrollBehavior;
      style.scrollBehavior = 'auto';
      window.scrollTo(0, Math.max(0, top));
      style.scrollBehavior = previous;
    };
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const deadline = performance.now() + 30000;
      let stableChecks = 0;
      const apply = () => {
        if (userTookOver) { finish(); return; }
        const productLink = returnPosition.product ? [...document.querySelectorAll('.sf-listing a[href*="/products/"]')].find((link) => {
          try { return new URL(link.href, window.location.href).pathname === returnPosition.product; } catch { return false; }
        }) : null;
        const card = productLink?.closest('[data-dx-card], .sf-card');
        const hasCardTarget = card && Number.isFinite(Number(returnPosition.cardOffset));
        const targetY = hasCardTarget
          ? window.scrollY + card.getBoundingClientRect().top - Number(returnPosition.cardOffset)
          : Number(returnPosition.y) || 0;
        jumpTo(targetY);
        requestAnimationFrame(() => {
          if (userTookOver) { finish(); return; }
          const cardError = hasCardTarget ? Math.abs(card.getBoundingClientRect().top - Number(returnPosition.cardOffset)) : Infinity;
          const yError = Math.abs(window.scrollY - Math.min(Number(returnPosition.y) || 0, Math.max(0, document.documentElement.scrollHeight - window.innerHeight)));
          if (document.readyState === 'complete' && Math.min(cardError, yError) < 8) stableChecks += 1;
          else stableChecks = 0;
          if (stableChecks >= 3 || performance.now() >= deadline) { finish(); return; }
          setTimeout(apply, 100);
        });
      };
      apply();
    }));
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (link && link.closest('.sf-listing') && /\/products\//.test(link.pathname || link.href)) saveReturnPosition(link);
  }, true);
  window.addEventListener('pageshow', (event) => { if (event.persisted) restoreReturnPosition(true); });
  // Native Shopify filters in the URL: leave the server page exactly as it is.
  if ([...new URLSearchParams(window.location.search).keys()].some((k) => k.startsWith('filter.'))) { restoreReturnPosition(); return; }

  const form = document.getElementById('FilterForm');
  const aside = document.getElementById('FilterDrawer');
  const main = form?.querySelector('.sf-listing__main');
  const facetsHost = aside?.querySelector('[data-dx-facets]');
  const nativeFacets = aside?.querySelector('[data-native-facets]');
  if (!form || !aside || !main || !facetsHost) return;
  const runtime = (() => { try { return JSON.parse(document.getElementById('ExxenRuntimeData')?.textContent || '{}'); } catch { return {}; } })();
  const S = config.strings || {};
  const sortSelect = form.querySelector('select[name="sort_by"]');
  const countNode = main.querySelector('.sf-toolbar__count');
  const filterButton = form.querySelector('.sf-toolbar__filter');
  const applyButton = aside.querySelector('.sf-filters__actions button[type="submit"]');
  const clearLink = aside.querySelector('.sf-filters__actions a');
  const sortStatus = main.querySelector('[data-sort-status]');
  function showSortStatus() {
    if (sortStatus) { sortStatus.hidden = false; sortStatus.dataset.state = 'loading'; }
    main.setAttribute('aria-busy', 'true');
  }
  function hideSortStatus() {
    if (sortStatus) { sortStatus.hidden = true; delete sortStatus.dataset.state; }
  }
  function clearNetworkError() { main.querySelector('[data-dx-error]')?.remove(); }
  function showNetworkError() {
    clearNetworkError();
    hideSortStatus();
    main.removeAttribute('aria-busy');
    const alert = document.createElement('div');
    alert.className = 'sf-empty sf-dx-error';
    alert.dataset.dxError = '';
    alert.setAttribute('role', 'alert');
    alert.innerHTML = `<h2>${Core.esc(S.networkErrorTitle)}</h2><p>${Core.esc(S.networkErrorText)}</p><button type="button" class="button" data-dx-retry>${Core.esc(S.retry)}</button>`;
    main.querySelector('.sf-toolbar')?.insertAdjacentElement('afterend', alert);
  }

  let data = null;
  let ctx = null;
  let state = null;
  let result = null;
  let ix = null;
  const orders = {};
  // What the engine did, for the browser tests: mode (index | live), why it
  // went live, requests and cache hits per kind.
  const stats = { mode: 'boot', reason: null, requests: { index: 0, chunk: 0, order: 0, live: 0 }, cache: { index: 0, chunk: 0, order: 0 }, chunks: [], prefetched: [] };
  root.ExxenDiscoveryStats = stats;
  const setMode = (mode) => { stats.mode = mode; form.dataset.dxMode = mode; };
  const disabledKey = `sf-dx-disabled:${config.handle}:${config.index || 'none'}`;

  async function fetchJson(url, { signal, timeoutMs = 10000 } = {}) {
    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json,text/plain,*/*' }, signal: controller.signal });
        if (response.status === 429 || response.status >= 500) throw new Error(`HTTP ${response.status}`);
        if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { fatal: true });
        return JSON.parse(await response.text());
      } catch (error) {
        lastError = error;
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (error.fatal || attempt === 2) break;
        await new Promise((resolve, reject) => {
          const wait = setTimeout(resolve, 500 * (2 ** attempt));
          signal?.addEventListener('abort', () => { clearTimeout(wait); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
        });
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
      }
    }
    throw lastError;
  }
  // Every page of a paged view in one round: the page count is known from the
  // server-rendered total (250 per page), so page 1 is not awaited before the
  // others. Page 1 still has the last word: a larger count (products added
  // since render) is fetched afterwards, a smaller one drops the empty tail.
  const LIVE_PAGE = 250;
  const LIVE_PARALLEL = 6;
  async function fetchPaged(base, kind, signal) {
    const out = [];
    const fetchPages = async (from, to) => {
      let next = from;
      const worker = async () => {
        while (next <= to) {
          const n = next; next += 1;
          stats.requests[kind] += 1;
          out[n - 1] = await fetchJson(`${base}&page=${n}`, { signal });
        }
      };
      await Promise.all(Array.from({ length: Math.min(LIVE_PARALLEL, to - from + 1) }, worker));
    };
    const guess = Math.max(1, Math.ceil((Number(config.total) || 0) / LIVE_PAGE));
    await fetchPages(1, guess);
    const pages = Math.max(1, Number(out[0].pages) || 1);
    if (pages > guess) await fetchPages(guess + 1, pages);
    return out.slice(0, pages);
  }

  // Live path (the index disagrees with the rendered page): the whole
  // collection's live data, kept for a few minutes in sessionStorage so
  // returning from a product page does not load it again. Only the current
  // collection is kept; storage errors just skip the cache.
  const CACHE_PREFIX = 'sf-dx-live:';
  const CACHE_TTL = 5 * 60 * 1000;
  const CACHE_MAX_CHARS = 2500000;
  function cacheRead(key) {
    try {
      const raw = window.sessionStorage.getItem(CACHE_PREFIX + key);
      const hit = raw && JSON.parse(raw);
      return hit && Date.now() - hit.t < CACHE_TTL && hit.total === Number(config.total) ? hit.v : null;
    } catch { return null; }
  }
  function cacheWrite(key, value) {
    try {
      const raw = JSON.stringify({ t: Date.now(), total: Number(config.total), v: value });
      for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX) && !k.startsWith(CACHE_PREFIX + config.data)) window.sessionStorage.removeItem(k);
      }
      if (raw.length <= CACHE_MAX_CHARS) window.sessionStorage.setItem(CACHE_PREFIX + key, raw);
    } catch { /* quota or privacy mode */ }
  }
  async function loadLive() {
    const base = config.data;
    const cached = cacheRead(base);
    if (cached) return cached;
    const out = await fetchPaged(base, 'live');
    const first = out[0];
    const live = { products: out.flatMap((page) => (page && page.p) || []), sort: first.sort, size: first.size, total: first.total };
    cacheWrite(base, live);
    return live;
  }

  // Index path cache (sessionStorage, one bounded budget): the index keyed on
  // its asset URL (asset_url's ?v= changes with every upload), card chunks on
  // their signature, server orders for 5 minutes. Oldest entries go first;
  // storage errors just skip the cache.
  const STORE = 'sf-dx3:';
  const STORE_MAX = 1500000;
  const ORDER_TTL = 5 * 60 * 1000;
  function storeGet(key) {
    try { const raw = window.sessionStorage.getItem(STORE + key); return raw ? JSON.parse(raw) : null; } catch { return null; }
  }
  function storeSet(key, value) {
    try {
      const raw = JSON.stringify(value);
      if (raw.length > STORE_MAX / 3) return;
      const meta = storeGet('~') || {};
      delete meta[key];
      let used = raw.length;
      for (const k of Object.keys(meta)) used += meta[k][1];
      for (const k of Object.keys(meta).sort((a, b) => meta[a][0] - meta[b][0])) {
        if (used <= STORE_MAX) break;
        used -= meta[k][1];
        delete meta[k];
        window.sessionStorage.removeItem(STORE + k);
      }
      meta[key] = [Date.now(), raw.length];
      window.sessionStorage.setItem(STORE + key, raw);
      window.sessionStorage.setItem(`${STORE}~`, JSON.stringify(meta));
    } catch { /* quota or privacy mode */ }
  }

  async function loadIndex() {
    const key = `ix:${config.handle}`;
    const hit = storeGet(key);
    if (hit && hit.src === config.index && hit.body && hit.body.v === 3) { stats.cache.index += 1; return hit.body; }
    stats.requests.index += 1;
    const body = await fetchJson(config.index);
    if (!body || body.v !== 3 || body.h !== config.handle || !Array.isArray(body.id) || body.id.length !== body.n) throw new Error('index format');
    storeSet(key, { src: config.index, body });
    return body;
  }

  // Card chunks: fetched only for the cards on screen, at most 4 at a time;
  // the URL carries the chunk's signature (browser cache key) and the body
  // must carry it too, or the chunk is fetched once more past the caches.
  const CHUNK_PARALLEL = 4;
  const CHUNK_MEMORY = 48;
  const chunkLoads = new Map();
  let cardBase = null;
  const chunkUrl = (k, sig, bust) => {
    const suffix = ix.cards.addressed ? sig : String(k).padStart(3, '0');
    return `${String(config.card0).replace(/-000\.json(\?.*)?$/, `-${suffix}.json`)}?v=${sig}${bust ? `&r=${bust}` : ''}`;
  };
  function applyChunk(k, body) {
    const positions = Core.chunkPositions(k, ix);
    if (!body || body.k !== k || body.sig !== ix.cards.sig[k] || !Array.isArray(body.r) || body.r.length !== positions.length) return false;
    positions.forEach((pos, slot) => { const it = data.items[pos]; if (!it.L) it.L = Core.cardFromRow(body.r[slot], it, cardBase); });
    return true;
  }
  function loadChunk(k) {
    if (chunkLoads.has(k)) return chunkLoads.get(k);
    const sig = ix.cards.sig[k];
    const run = (async () => {
      const key = `ck:${config.handle}:${k}`;
      const hit = storeGet(key);
      if (hit && hit.sig === sig && applyChunk(k, hit.body)) { stats.cache.chunk += 1; return; }
      for (const bust of [0, Date.now()]) {
        stats.requests.chunk += 1;
        stats.chunks.push(k);
        const body = await fetchJson(chunkUrl(k, sig, bust));
        if (applyChunk(k, body)) { storeSet(key, { sig, body }); return; }
      }
      throw new Error(`chunk ${k} signature`);
    })();
    chunkLoads.set(k, run);
    run.catch(() => chunkLoads.delete(k));
    if (chunkLoads.size > CHUNK_MEMORY) chunkLoads.delete(chunkLoads.keys().next().value);
    return run;
  }
  async function ensureCards(items) {
    const need = [...new Set(items.filter((it) => it && !it.L).map((it) => Core.chunkOf(it.pos, ix)))];
    let next = 0;
    const worker = async () => { while (next < need.length) { const k = need[next]; next += 1; await loadChunk(k); } };
    await Promise.all(Array.from({ length: Math.min(CHUNK_PARALLEL, need.length) }, worker));
    checkPage();
  }

  // The index is built from a snapshot of the live collection; the rendered
  // page is live. Its count, in-stock count and products (price, compare-at,
  // stock, position; link and title once their cards are loaded) must agree
  // with the index, or the live path takes over.
  function drift(index, decoded) {
    const live = config.live || {};
    if (Number(config.total) !== index.n) return 'total';
    if (config.defaultSort && index.sort && config.defaultSort !== index.sort) return 'default-sort';
    if (live.inStock !== null && live.inStock !== undefined && live.inStock !== decoded.items.filter((it) => it.available).length) return 'in-stock';
    const ordered = !live.sort || live.sort === index.sort;
    for (const [i, [id, price, cp, a]] of (live.page || []).entries()) {
      const it = decoded.byId.get(id);
      if (!it) return 'member';
      if (it.cents !== price || it.cp !== cp || (it.available ? 1 : 0) !== a) return 'commerce';
      if (ordered && it.pos !== (live.offset || 0) + i) return 'order';
    }
    return null;
  }
  async function sha10(text) {
    const digest = await window.crypto.subtle.digest('SHA-1', new TextEncoder().encode(text));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 10);
  }
  let pageChecked = false;
  function checkPage() {
    if (pageChecked || !data || data.mode !== 'index') return;
    const entries = (config.live && config.live.page) || [];
    const items = entries.map(([id]) => data.byId.get(id));
    if (items.some((it) => !it || !it.L)) return;
    pageChecked = true;
    if (entries.some((e, i) => items[i].L.u !== e[4])) { toLive('link'); return; }
    if (!window.crypto?.subtle || !window.TextEncoder) return;
    Promise.all(entries.map((e, i) => sha10(items[i].L.t).then((h) => h === e[5])))
      .then((ok) => { if (ok.includes(false)) toLive('title'); })
      .catch(() => { /* no digest: the other checks stand */ });
  }
  async function liveData(reason, locale) {
    stats.reason = reason;
    const live = await loadLive();
    const decoded = Core.buildLiveItems(ix, live.products, locale);
    decoded.sort = live.sort;
    setMode('live');
    return decoded;
  }
  let liveSwitch = null;
  function toLive(reason) {
    if (liveSwitch) return liveSwitch;
    stats.reason = reason;
    try { window.sessionStorage.setItem(disabledKey, '1'); } catch { /* storage unavailable */ }
    form.removeAttribute('data-dx-active');
    setMode('native-stale');
    liveSwitch = Promise.resolve().then(() => window.location.reload());
    return liveSwitch;
  }

  function wishlistSet() {
    try { return new Set((window.ExxenStore?.wishlist?.items() || []).map((item) => item.handle)); } catch { return new Set(); }
  }
  const pushUrl = (mode) => {
    const query = Core.toQuery(state, ctx.facets, window.location.search, ctx.defaultSort);
    const url = `${window.location.pathname}${query}${window.location.hash}`;
    if (url === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;
    try { history[mode === 'push' ? 'pushState' : 'replaceState']({ dx: true }, '', url); } catch { /* history unavailable */ }
  };

  // --------------------------------------------------------------- facets UI
  function buildFacets() {
    const parts = [];
    const priority = ['brand', 'group', 'price', 'w', 'h', 'd'];
    const orderedFacets = [...ctx.facets].sort((a, b) => {
      const ai = priority.indexOf(a); const bi = priority.indexOf(b);
      return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
    });
    orderedFacets.forEach((key) => {
      const def = Core.FACETS[key];
      if (!def) return;
      const label = ctx.labels[key] || key;
      const open = key === 'brand' || key === 'group' || state.f[key] !== undefined ? ' open' : '';
      if (def.kind === 'range') {
        const presets = Core.presets(key, data);
        parts.push(`<details class="sf-filter" data-dx-facet="${key}"${open}><summary>${Core.esc(label)}${icon('chevron')}</summary><div class="sf-filter__options">`
          + (presets.length ? `<div class="sf-dx-presets">${presets.map((p) => `<button type="button" class="sf-dx-preset" data-dx-preset="${key}" data-min="${p.min}" data-max="${p.max ?? ''}" aria-pressed="false">${Core.esc(p.max === null ? `${p.min}+` : `${p.min}–${p.max}`)}</button>`).join('')}</div>` : '')
          + `<div class="sf-range"><label><span>${Core.esc(S.min)}</span><input type="number" data-dx-range="${key}" data-edge="min" min="0" step="1" inputmode="numeric"></label><span aria-hidden="true">–</span><label><span>${Core.esc(S.max)}</span><input type="number" data-dx-range="${key}" data-edge="max" min="0" step="1" inputmode="numeric"></label></div></div></details>`);
      } else if (def.kind === 'bool') {
        parts.push(`<div class="sf-filter sf-dx-bool" data-dx-facet="${key}"><div class="sf-filter__options"><label><input type="checkbox" data-dx-bool="${key}"><span>${Core.esc(label)}</span><small data-dx-count></small></label></div></div>`);
      } else {
        const all = {};
        for (const it of data.items) for (const v of def.get(it)) all[v] = (all[v] || 0) + 1;
        const values = Core.orderValues(key, Object.keys(all), all, ctx);
        const colors = key === 'color';
        parts.push(`<details class="sf-filter" data-dx-facet="${key}"${open}><summary>${Core.esc(label)}${icon('chevron')}</summary><div class="sf-filter__options${colors ? ' sf-filter__options--colors' : ''}" data-filter-options data-collapsed="true">${values.map((v) => {
          const swatch = colors && ctx.colors[v] ? `<span class="sf-swatch" style="--swatch:${Core.esc(ctx.colors[v][1])}" aria-hidden="true"></span>` : '';
          return `<label data-dx-value="${Core.esc(v)}"><input type="checkbox" data-dx-list="${key}" value="${Core.esc(v)}">${swatch}<span>${Core.esc(Core.valueLabel(key, v, ctx))}</span><small data-dx-count></small></label>`;
        }).join('')}</div>${values.length > 6 ? `<button type="button" class="sf-filter__toggle" data-filter-toggle data-more="${Core.esc(S.showMore)}" data-less="${Core.esc(S.showLess)}" aria-expanded="false">${Core.esc(S.showMore)}</button>` : ''}</details>`);
      }
    });
    facetsHost.innerHTML = parts.join('');
  }
  const icon = (name) => `<svg class="sf-i" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href="#i-${name}"></use></svg>`;

  function syncFacets() {
    for (const el of facetsHost.querySelectorAll('[data-dx-facet]')) {
      const key = el.dataset.dxFacet;
      const info = result.info[key];
      const val = state.f[key];
      if (!info) continue;
      if (info.kind === 'range') {
        const lo = el.querySelector('[data-edge="min"]'); const hi = el.querySelector('[data-edge="max"]');
        const round = (n, up) => (n === null ? '' : String(up ? Math.ceil(n) : Math.floor(n)));
        lo.placeholder = round(info.min, false); hi.placeholder = round(info.max, true);
        if (document.activeElement !== lo) lo.value = val && val.min !== null && val.min !== undefined ? String(val.min) : '';
        if (document.activeElement !== hi) hi.value = val && val.max !== null && val.max !== undefined ? String(val.max) : '';
        el.querySelectorAll('[data-dx-preset]').forEach((b) => {
          const pmin = Number(b.dataset.min); const pmax = b.dataset.max === '' ? null : Number(b.dataset.max);
          b.setAttribute('aria-pressed', String(!!val && val.min === pmin && (val.max ?? null) === pmax));
        });
        const coverageTotal = result.baseTotal[key] || data.items.length || 0;
        const usefulCoverage = info.count >= 10 && coverageTotal > 0 && info.count / coverageTotal >= 0.20;
        el.hidden = !val && !usefulCoverage;
      } else if (info.kind === 'bool') {
        const box = el.querySelector('input');
        box.checked = val === true;
        el.querySelector('[data-dx-count]').textContent = info.count;
        el.hidden = info.count === 0 && !val;
      } else {
        let visible = 0;
        el.querySelectorAll('[data-dx-value]').forEach((label) => {
          const v = label.dataset.dxValue;
          const n = info.counts[v] || 0;
          const on = Array.isArray(val) && val.includes(v);
          label.querySelector('input').checked = on;
          label.querySelector('[data-dx-count]').textContent = n;
          label.hidden = n === 0 && !on;
          if (!label.hidden) visible += 1;
        });
        el.hidden = visible < 1;
        const options = el.querySelector('[data-filter-options]');
        if (options) options.dataset.hasOverflow = String(visible > 6);
        const toggle = el.querySelector('[data-filter-toggle]');
        if (toggle) toggle.hidden = visible <= 6;
      }
    }
  }

  // ---------------------------------------------------------------- results
  function results() {
    let host = main.querySelector('[data-dx-results]');
    if (!host) {
      main.querySelectorAll(':scope > .sf-active-filters, :scope > .sf-grid, :scope > .sf-empty, :scope > .sf-pagination').forEach((node) => node.remove());
      host = document.createElement('div');
      host.dataset.dxResults = '';
      main.appendChild(host);
    }
    return host;
  }
  function pageHref(n) {
    return `${window.location.pathname}${Core.toQuery({ ...state, page: n }, ctx.facets, window.location.search, ctx.defaultSort)}`;
  }
  function renderPagination() {
    const total = result.pages; const current = result.page;
    if (total < 2) return '';
    const keep = (i) => i === 1 || i === total || Math.abs(i - current) <= 1;
    let html = `<nav class="sf-pagination" aria-label="${Core.esc(S.pages)}">`;
    if (current > 1) html += `<a href="${Core.esc(pageHref(current - 1))}" data-dx-page="${current - 1}">${Core.esc(S.prevPage)}</a>`;
    let last = 0;
    for (let i = 1; i <= total; i += 1) {
      if (!keep(i)) continue;
      if (last > 0 && i - last > 1) html += '<span class="sf-pagination__gap" aria-hidden="true">…</span>';
      html += i === current ? `<span aria-current="page">${i}</span>` : `<a href="${Core.esc(pageHref(i))}" data-dx-page="${i}">${i}</a>`;
      last = i;
    }
    if (current < total) html += `<a href="${Core.esc(pageHref(current + 1))}" data-dx-page="${current + 1}">${Core.esc(S.nextPage)}</a>`;
    return `${html}</nav>`;
  }
  function renderChips() {
    const list = Core.chips(state, ctx);
    if (!list.length) return '';
    return `<div class="sf-active-filters">${list.map((chip) => {
      const href = `${window.location.pathname}${Core.toQuery(Core.without(state, chip.key, chip.value), ctx.facets, window.location.search, ctx.defaultSort)}`;
      return `<a href="${Core.esc(href)}" class="sf-chip" data-dx-remove="${chip.key}"${chip.value !== undefined ? ` data-dx-remove-value="${Core.esc(chip.value)}"` : ''}>${Core.esc(chip.label)}<span aria-hidden="true">×</span><span class="visually-hidden">${Core.esc(S.removeFilter)}</span></a>`;
    }).join('')}<a class="sf-link sf-active-filters__clear" href="${Core.esc(config.url)}" data-dx-clear>${Core.esc(S.clearAll)}</a></div>`;
  }
  function renderEmpty() {
    const tips = Core.suggestions(data, state);
    const tipHtml = tips.length
      ? `<div class="sf-dx-suggest"><p>${Core.esc(S.suggestTitle)}</p><div>${tips.map((tip) => {
        const label = Core.chips({ f: { [tip.key]: state.f[tip.key] } }, ctx).map((c) => c.label).join(', ');
        return `<button type="button" class="sf-chip" data-dx-remove="${tip.key}">${Core.esc(Core.fill(S.suggestItem, { __L__: label, __N__: tip.count }))}</button>`;
      }).join('')}</div></div>`
      : '';
    return `<div class="sf-empty"><h2>${Core.esc(S.emptyTitle)}</h2><p>${Core.esc(S.emptyText)}</p>${tipHtml}<a class="button" href="${Core.esc(config.url)}" data-dx-clear>${Core.esc(S.clear)}</a></div>`;
  }
  function renderCount() {
    if (!countNode) return;
    const products = result.matched.length;
    const models = result.cards.length;
    const modelText = models === products ? '' : ` · ${models === 1 ? S.modelOne : Core.fill(S.models, { __N__: models })}`;
    countNode.innerHTML = `<strong>${products}</strong> ${Core.esc(S.productsUnit)}${Core.esc(modelText)}`;
  }
  function renderResults() {
    clearNetworkError();
    ctx.wishlist = wishlistSet();
    const host = results();
    const grid = result.pageCards.length
      ? `<div class="sf-grid sf-grid--listing">${result.pageCards.map((card, index) => Core.renderCard(card.rep, card.members, ctx, undefined, index < 4)).join('')}</div>`
      : renderEmpty();
    host.innerHTML = renderChips() + grid + renderPagination();
    main.removeAttribute('aria-busy');
    hideSortStatus();
    renderMeta();
  }
  // Count, filter badge, the drawer's "show N" button, clear link.
  function renderMeta() {
    renderCount();
    const active = Object.keys(state.f).length;
    if (filterButton) {
      let badge = filterButton.querySelector('.sf-dx-badge');
      if (active && !badge) { badge = document.createElement('span'); badge.className = 'sf-dx-badge'; filterButton.appendChild(badge); }
      if (badge) { badge.textContent = active ? String(active) : ''; badge.hidden = !active; badge.setAttribute('aria-label', Core.fill(S.selectedN, { __N__: active })); }
    }
    if (applyButton) applyButton.textContent = Core.fill(S.showN, { __N__: result.matched.length });
    if (clearLink) clearLink.hidden = !active;
  }

  // Filters, counts and the URL follow at once (index only); the cards of the
  // page follow as soon as their chunks are in. A newer update wins.
  let seq = 0;
  async function update(mode = 'replace') {
    const token = ++seq;
    const current = data;
    result = Core.compute(data, state, ctx);
    result.baseTotal = {};
    for (const key of ctx.facets) {
      if (Core.FACETS[key]?.kind !== 'range') continue;
      result.baseTotal[key] = Object.keys(state.f).some((k) => k !== key) ? data.items.filter((it) => Core.matches(it, state.f, key)).length : data.items.length;
    }
    state.page = result.page;
    syncFacets();
    renderMeta();
    pushUrl(mode);
    const reps = result.pageCards.map((card) => card.rep);
    if (reps.some((it) => !it.L)) {
      main.setAttribute('aria-busy', 'true');
      try {
        await ensureCards(reps);
      } catch (error) {
        if (token === seq) {
          main.removeAttribute('aria-busy');
          hideSortStatus();
          if (/signature/i.test(error?.message || '')) {
            const switched = toLive(`chunk: ${error.message}`);
            if (switched) switched.catch(() => showNetworkError());
          } else showNetworkError();
        }
        return;
      }
      if (token !== seq || current !== data) return;
    }
    renderResults();
    prefetchNext(token);
  }
  // When the browser is idle: the chunks of the next result page, one at a
  // time and at most four; nothing on save-data connections.
  const PREFETCH_MAX = 4;
  function prefetchNext(token) {
    if (data.mode !== 'index' || result.page >= result.pages) return;
    if (navigator.connection && navigator.connection.saveData) return;
    const size = ctx.pageSize || 24;
    const next = result.cards.slice(result.page * size, (result.page + 1) * size).map((card) => card.rep).filter((it) => !it.L);
    const ks = [...new Set(next.map((it) => Core.chunkOf(it.pos, ix)))].slice(0, PREFETCH_MAX);
    if (!ks.length) return;
    const idle = window.requestIdleCallback ? (fn) => window.requestIdleCallback(fn, { timeout: 3000 }) : (fn) => setTimeout(fn, 400);
    idle(async () => {
      for (const k of ks) {
        if (token !== seq) return;
        try { await loadChunk(k); stats.prefetched.push(k); } catch { return; }
      }
    });
  }
  function scrollToResults() {
    const top = form.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sf-masthead'), 10) || 120) - 8;
    if (window.scrollY > top) window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  // Sorts only Shopify can order (manual, best-selling) come from the server
  // once (ids only, ?view=sf-order) and are cached as a rank map; the client
  // never guesses them.
  const SERVER_SORTS = new Set(['manual', 'best-selling']);
  async function loadOrder(sort, signal) {
    const key = `ord:${config.handle}:${sort}`;
    const hit = storeGet(key);
    if (hit && Date.now() - hit.t < ORDER_TTL && hit.total === Number(config.total)) { stats.cache.order += 1; return hit.ids; }
    const pages = await fetchPaged(`${config.order}&sort_by=${encodeURIComponent(sort)}`, 'order', signal);
    const ids = pages.flatMap((page) => (page && page.i) || []);
    storeSet(key, { t: Date.now(), total: Number(config.total), ids });
    return ids;
  }
  async function ensureOrder(sort, signal) {
    if (!sort || !SERVER_SORTS.has(sort) || sort === data.sort || orders[sort]) return true;
    try {
      const ids = await loadOrder(sort, signal);
      orders[sort] = new Map(ids.map((id, i) => [id, i]));
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return false;
    }
  }

  // ---------------------------------------------------------------- events
  function setRange(key) {
    const el = facetsHost.querySelector(`[data-dx-facet="${key}"]`);
    const lo = el.querySelector('[data-edge="min"]').value; const hi = el.querySelector('[data-edge="max"]').value;
    const r = Core.parseRange(`${lo}-${hi}`);
    const f = { ...state.f };
    if (r) f[key] = r; else delete f[key];
    state = { ...state, f, page: 1 };
    update('push');
  }
  let rangeTimer = 0;
  facetsHost.addEventListener('input', (event) => {
    const input = event.target.closest('[data-dx-range]');
    if (!input) return;
    clearTimeout(rangeTimer);
    rangeTimer = setTimeout(() => setRange(input.dataset.dxRange), 650);
  });
  facetsHost.addEventListener('change', (event) => {
    event.stopPropagation();
    const target = event.target;
    if (target.matches('[data-dx-range]')) { clearTimeout(rangeTimer); setRange(target.dataset.dxRange); return; }
    const f = { ...state.f };
    if (target.matches('[data-dx-bool]')) {
      if (target.checked) f[target.dataset.dxBool] = true; else delete f[target.dataset.dxBool];
    } else if (target.matches('[data-dx-list]')) {
      const key = target.dataset.dxList;
      const set = new Set(f[key] || []);
      if (target.checked) set.add(target.value); else set.delete(target.value);
      if (set.size) f[key] = [...set]; else delete f[key];
    } else return;
    state = { ...state, f, page: 1 };
    update('push');
  });
  facetsHost.addEventListener('click', (event) => {
    const preset = event.target.closest('[data-dx-preset]');
    if (!preset) return;
    const key = preset.dataset.dxPreset;
    const f = { ...state.f };
    if (preset.getAttribute('aria-pressed') === 'true') delete f[key];
    else f[key] = { min: Number(preset.dataset.min), max: preset.dataset.max === '' ? null : Number(preset.dataset.max) };
    state = { ...state, f, page: 1 };
    update('push');
  });
  main.addEventListener('click', (event) => {
    const retry = event.target.closest('[data-dx-retry]');
    const remove = event.target.closest('[data-dx-remove]');
    const clear = event.target.closest('[data-dx-clear]');
    const page = event.target.closest('[data-dx-page]');
    const swatch = event.target.closest('[data-dx-swatch]');
    if (retry) {
      event.preventDefault();
      window.location.reload();
    } else if (remove) {
      event.preventDefault();
      state = Core.without(state, remove.dataset.dxRemove, remove.dataset.dxRemoveValue);
      update('push');
    } else if (clear) {
      event.preventDefault();
      state = { ...state, f: {}, page: 1 };
      update('push');
    } else if (page) {
      event.preventDefault();
      state = { ...state, page: Number(page.dataset.dxPage) };
      update('push');
      scrollToResults();
    } else if (swatch) {
      event.preventDefault();
      const card = swatch.closest('[data-dx-card]');
      const member = data.byId.get(Number(swatch.dataset.dxSwatch));
      const entry = result.cards.find((c) => c.members.includes(member));
      if (!card || !member || !entry) return;
      const show = () => {
        if (!card.isConnected) return;
        ctx.wishlist = wishlistSet();
        card.outerHTML = Core.renderCard(member, entry.members, ctx, member.id);
        main.querySelector(`[data-dx-swatch="${member.id}"]`)?.focus();
      };
      if (member.L) show();
      else ensureCards([member]).then(show).catch(() => { /* the card stays as it is */ });
    }
  });
  clearLink?.addEventListener('click', (event) => {
    event.preventDefault();
    state = { ...state, f: {}, page: 1 };
    update('push');
  });
  form.addEventListener('submit', (event) => {
    // In native / native-variants mode Shopify owns this form. Do not swallow
    // its submit (notably the mobile “Apply” button).
    if (!form.hasAttribute('data-dx-active')) return;
    event.preventDefault();
    clearTimeout(rangeTimer);
    facetsHost.querySelectorAll('[data-dx-facet]').forEach((el) => { if (el.querySelector('[data-dx-range]')) setRange(el.dataset.dxFacet); });
    if (aside.dataset.open === 'true') aside.querySelector('[data-drawer-close]')?.click();
    scrollToResults();
  });
  let sortSeq = 0;
  let sortController = null;
  sortSelect?.addEventListener('change', async (event) => {
    if (!data || !form.hasAttribute('data-dx-active')) return;
    showSortStatus();
    event.stopPropagation();
    const sort = sortSelect.value;
    const token = ++sortSeq;
    sortController?.abort();
    sortController = new AbortController();
    let loaded;
    try { loaded = await ensureOrder(sort, sortController.signal); }
    catch (error) { if (error?.name === 'AbortError') return; throw error; }
    if (token !== sortSeq) return;
    if (!loaded) { main.removeAttribute('aria-busy'); hideSortStatus(); showNetworkError(); return; }
    state = { ...state, sort, page: 1 };
    update('push');
  });
  window.addEventListener('popstate', () => {
    if (!data) return;
    state = Core.parseQuery(window.location.search, ctx.facets);
    if (!state.sort) state.sort = ctx.defaultSort;
    if (sortSelect) sortSelect.value = state.sort;
    const token = ++sortSeq;
    sortController?.abort();
    sortController = new AbortController();
    showSortStatus();
    ensureOrder(state.sort, sortController.signal).then((loaded) => {
      if (token !== sortSeq) return;
      if (loaded) update('none'); else { main.removeAttribute('aria-busy'); hideSortStatus(); showNetworkError(); }
    }).catch((error) => { if (error?.name !== 'AbortError') { main.removeAttribute('aria-busy'); hideSortStatus(); showNetworkError(); } });
  });

  // ---------------------------------------------------------------- boot
  async function boot() {
    const pending = Core.parseQuery(window.location.search, [...Object.keys(Core.FACETS)]);
    const hasOwn = Object.keys(pending.f).length > 0;
    try { if (window.sessionStorage.getItem(disabledKey) === '1') { setMode('native-stale'); restoreReturnPosition(); return; } } catch { /* storage unavailable */ }
    if (hasOwn) {
      main.setAttribute('aria-busy', 'true');
      if (countNode) countNode.textContent = S.loading;
    }
    try {
      if (!config.index || !config.card0 || !config.order) throw new Error('no index');
      const locale = runtime.shop?.locale || document.documentElement.lang;
      ix = await loadIndex();
      if (!Array.isArray(ix.cards?.sig)) {
        const cardLocale = String(locale || 'bg').toLowerCase().split('-')[0];
        ix.cards.sig = ix.cards.sig?.[cardLocale] || ix.cards.sig?.bg || ix.cards.sig?.tr;
      }
    cardBase = { productBase: config.productBase || '/products/', img: ix.img, locale: String(locale || 'bg').toLowerCase().split('-')[0] };
      const decoded = Core.decodeIndex(ix, locale);
      const reason = drift(ix, decoded);
      if (reason) {
        stats.reason = reason;
        try { window.sessionStorage.setItem(disabledKey, '1'); } catch { /* storage unavailable */ }
        setMode('native-stale');
        main.removeAttribute('aria-busy');
        restoreReturnPosition();
        return;
      }
      data = decoded; data.sort = ix.sort; setMode('index');
      ctx = {
        facets: (ix.facets || []).filter((key) => Core.FACETS[key]),
        // The index carries Bulgarian colour names; the config may rename them for the storefront language.
        colors: Object.fromEntries(Object.entries(ix.colors || {}).map(([k, v]) => [k, config.colorNames && config.colorNames[k] ? [config.colorNames[k], v[1]] : v])), series: ix.series || {},
        groups: config.groups || {}, rooms: config.rooms || {},
        labels: config.labels || {}, enums: config.enums || {}, strings: S,
        pageSize: config.pageSize || 24, defaultSort: data.sort || config.defaultSort,
        moneyFormat: runtime.shop?.moneyFormat, locale,
        placeholder: runtime.assets?.placeholder || '', notPhoto: runtime.images?.notPhotoKeys || [], orders
      };
      state = Core.parseQuery(window.location.search, ctx.facets);
      if (!state.sort) state.sort = ctx.defaultSort;
      if (!await ensureOrder(state.sort)) throw new Error('sort order unavailable');
      form.setAttribute('data-dx-active', '');
      if (nativeFacets) nativeFacets.hidden = true;
      buildFacets();
      facetsHost.hidden = false;
      await update();
      restoreReturnPosition();
    } catch (error) {
      // The server-rendered listing and native filters stay in charge.
      main.removeAttribute('aria-busy');
      setMode('native');
      if (hasOwn) showNetworkError();
      if (countNode && hasOwn) countNode.innerHTML = `<strong>${config.total}</strong> ${Core.esc(S.productsUnit)}`;
      if (window.console) console.warn('[sf-discovery] native filters kept:', error && error.message);
      restoreReturnPosition();
    }
  }
  boot();
})();
